import React from 'react';
import { DatabaseConfig } from '../../../types/database.type';
import { getInstanceTypes } from '../../../constants/databaseEngine';
import InstanceTypeCard from '../components/InstanceTypeCard';
import { PricingPlan } from '../../../services/pricing.service';

interface Step2Props {
  config: DatabaseConfig;
  setConfig: (config: DatabaseConfig) => void;
  plans?: PricingPlan[];
  selectedPlanCode?: string | null;
  isLoadingPlans?: boolean;
  onSelectPlan?: (planCode: string, instanceClass: string) => void;
}

const Step2Configuration: React.FC<Step2Props> = ({
  config, setConfig, plans = [], selectedPlanCode, isLoadingPlans, onSelectPlan,
}) => {
  const isMongoDb = config.engine === 'mongodb';
  const usePricingAPI = plans.length > 0;
  const availableInstanceTypes = getInstanceTypes(config.engine);

  const formatPrice = (amount: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(amount / 100);

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Plan *</label>

        {isLoadingPlans && (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-4">Cargando planes disponibles…</div>
        )}

        {/* Planes desde catálogo de precios */}
        {!isLoadingPlans && usePricingAPI && (
          <div className="grid grid-cols-1 gap-3">
            {[...plans].sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99)).map((plan) => {
              const instanceClass = (plan.providerConfiguration as any)?.instanceClass || plan.planCode;
              const ram = plan.includedResources?.memoryMb ? `${plan.includedResources.memoryMb / 1024} GB` : '';
              const vcpu = plan.includedResources?.vcpu ? `${plan.includedResources.vcpu} vCPUs` : '';
              const storage = plan.includedResources?.storageGb ? `${plan.includedResources.storageGb} GB` : '';
              const isSelected = selectedPlanCode === plan.planCode;
              return (
                <button
                  key={plan.planCode}
                  type="button"
                  onClick={() => onSelectPlan?.(plan.planCode, instanceClass)}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                    isSelected
                      ? 'border-blue-600 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm">{plan.planName || plan.planCode}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {instanceClass}{vcpu && ` · ${vcpu}`}{ram && ` · ${ram} RAM`}{storage && ` · ${storage} SSD`}
                      </p>
                    </div>
                    <div className="text-right ml-4 shrink-0">
                      <p className="font-bold text-gray-900 dark:text-white text-sm">
                        {plan.priceAmount != null ? formatPrice(plan.priceAmount) : 'Consultar'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">/ mes</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Fallback: lista hardcodeada cuando no hay planes en el catálogo */}
        {!isLoadingPlans && !usePricingAPI && (
          <>
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                General Purpose {isMongoDb && '(EC2)'}
              </h4>
              <div className="grid grid-cols-1 gap-3">
                {availableInstanceTypes
                  .filter((i) => i.category === 'general')
                  .map((instance) => (
                    <InstanceTypeCard
                      key={instance.id}
                      instance={instance}
                      selected={config.instanceType === instance.id}
                      onClick={() => setConfig({ ...config, instanceType: instance.id })}
                    />
                  ))}
              </div>
            </div>
            {availableInstanceTypes.some((i) => i.category === 'memory') && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Memory Optimized</h4>
                <div className="grid grid-cols-1 gap-3">
                  {availableInstanceTypes
                    .filter((i) => i.category === 'memory')
                    .map((instance) => (
                      <InstanceTypeCard
                        key={instance.id}
                        instance={instance}
                        selected={config.instanceType === instance.id}
                        onClick={() => setConfig({ ...config, instanceType: instance.id })}
                      />
                    ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Storage: lo define el plan, no es configurable por el cliente. */}
      <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900 dark:text-white text-sm">Almacenamiento incluido</p>
            <p className="text-xs text-gray-500 mt-0.5">Definido por el plan seleccionado</p>
          </div>
          <p className="font-bold text-gray-900 dark:text-white">{config.storage} GB</p>
        </div>
      </div>

      {/* Backups automáticos: siempre activos en BD administradas (RDS). */}
      {!isMongoDb && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-sm font-medium text-green-900 dark:text-green-200 mb-1">Backups automáticos incluidos</p>
          <p className="text-xs text-green-800 dark:text-green-300">
            Snapshots diarios con retención de 7 días, activados por defecto. Sin configuración adicional.
          </p>
        </div>
      )}

      {isMongoDb && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-1">MongoDB on EC2</p>
          <p className="text-xs text-amber-800 dark:text-amber-300">
            MongoDB runs on a dedicated EC2 instance. Backups can be configured after deployment using mongodump or EBS snapshots. High availability via replica sets can be set up separately.
          </p>
        </div>
      )}
    </div>
  );
};

export default Step2Configuration;